import React from 'react';
import {
  IconStar,
  IconFireAlt
} from './icons';

/**
 * Composant Badge "Nouveau" pour les projets
 */
export const NewProjectBadge = ({ 
  type = 'default',
  size = 'md', 
  animated = true,
  className = '',
  onClick = null 
}) => {
  const getTypeStyles = () => {
    const baseStyles = "inline-flex items-center justify-center font-bold rounded-full border-2 transition-all duration-300";
    
    switch (type) {
      case 'premium':
        return `${baseStyles} bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-yellow-300 shadow-lg`;
      case 'featured':
        return `${baseStyles} bg-gradient-to-r from-purple-500 to-pink-500 text-white border-purple-300`;
      case 'hot':
        return `${baseStyles} bg-gradient-to-r from-red-500 to-red-600 text-white border-red-400`;
      case 'trending':
        return `${baseStyles} bg-gradient-to-r from-emerald-500 to-teal-600 text-white border-emerald-400`;
      default:
        return `${baseStyles} bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-400 shadow-md`;
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return 'px-2 py-1 text-xs min-w-[50px]';
      case 'lg':
        return 'px-4 py-2 text-sm min-w-[80px]';
      case 'xl':
        return 'px-5 py-3 text-base min-w-[100px]';
      default: // md
        return 'px-3 py-1.5 text-xs min-w-[65px]';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'premium':
        return <IconStar className="mr-1" />;
      case 'hot':
        return <IconFireAlt className="mr-1" />;
      case 'featured':
        return <IconStar className="mr-1" />;
      case 'trending':
        return <IconFireAlt className="mr-1" />;
      default:
        return <IconStar className="mr-1" />;
    }
  };

  const getText = () => {
    switch (type) {
      case 'premium':
        return 'PREMIUM';
      case 'featured':
        return 'VEDETTE';
      case 'hot':
        return 'CHAUD';
      case 'trending':
        return 'TENDANCE';
      default:
        return 'NOUVEAU';
    }
  };

  const animationClass = animated ? 'animate-pulse hover:animate-none hover:scale-105' : '';
  
  return (
    <div 
      className={`
        ${getTypeStyles()} 
        ${getSizeStyles()} 
        ${animationClass} 
        ${onClick ? 'cursor-pointer hover:shadow-lg' : ''} 
        ${className}
      `}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {getIcon()}
      <span>{getText()}</span>
    </div>
  );
};

/**
 * Badge compact pour les listes
 */
export const NewProjectDot = ({ 
  isNew = false, 
  count = 0,
  className = '',
  size = 'md' 
}) => {
  if (!isNew && count === 0) return null;

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-2 h-2';
      case 'lg':
        return 'w-4 h-4';
      default:
        return 'w-3 h-3';
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className={`
        ${getSizeClasses()}
        bg-red-500 
        rounded-full 
        animate-pulse
        ring-2 
        ring-white 
        shadow-lg
      `} />
      {count > 0 && (
        <div className="
          absolute -top-1 -right-1 
          bg-red-600 text-white 
          text-xs font-bold 
          rounded-full 
          min-w-[16px] h-4 
          flex items-center justify-center 
          px-1
        ">
          {count > 99 ? '99+' : count}
        </div>
      )}
    </div>
  );
};

/**
 * Bannière pour les nouveaux projets
 */
export const NewProjectBanner = ({ 
  count = 0, 
  onViewAll = null,
  className = '' 
}) => {
  if (count === 0) return null;

  return (
    <div className={`
      bg-gradient-to-r from-blue-50 to-emerald-50 
      border border-blue-200 
      rounded-lg p-4 mb-6 
      ${className}
    `}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <NewProjectDot isNew={true} count={count} size="lg" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              {count === 1 ? 'Nouveau projet disponible !' : `${count} nouveaux projets disponibles !`}
            </h3>
            <p className="text-sm text-gray-600">
              Découvrez {count === 1 ? 'le dernier projet ajouté' : 'les derniers projets ajoutés'} à la plateforme
            </p>
          </div>
        </div>
        {onViewAll && (
          <button
            onClick={onViewAll}
            className="
              bg-blue-500 hover:bg-blue-600 
              text-white font-medium 
              px-4 py-2 rounded-lg 
              transition-colors duration-200
              flex items-center space-x-2
            "
          >
            <IconStar />
            <span>Voir tout</span>
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Overlay pour marquer visuellement les projets nouveaux
 */
export const NewProjectOverlay = ({ children, isNew = false }) => {
  if (!isNew) return children;

  return (
    <div className="relative">
      {children}
      <div className="absolute top-2 right-2 z-10">
        <NewProjectBadge size="sm" animated={true} />
      </div>
      <div className="absolute inset-0 ring-2 ring-blue-400 ring-opacity-50 rounded-lg pointer-events-none" />
    </div>
  );
};

export default NewProjectBadge;